import Money from 'common/Money';
import * as React from 'react';
import {toDisplayBalance} from 'src/account/balanceDisplay';
import {useMoneyFormatter} from 'src/hooks/useUnits';
import {cn} from 'src/lib/utils';

type Props = {
  /** ISO date of the baseline anchor, shown next to its balance. */
  baselineDate: string | null;
  baselineBalance: number;
  /** ISO date of the recorded anchor, shown next to its balance. */
  recordedDate?: string | null;
  /** No prior anchor exists — balances render as em dashes. */
  needsStartingBalance: boolean;
  selectedSum: number;
  recordedBalance: number;
  isBalanced: boolean;
  /** Sums are still partial (pages streaming in) — show placeholders. */
  pending: boolean;
  /**
   * Credit card amounts are sign-flipped so the summary reads as "amount
   * owed" (prev owed + new charges = new owed). The reconciliation math
   * itself still runs on the raw signed values.
   */
  isCreditCard: boolean;
  currency: string;
};

/**
 * The reconciliation math block shared by the reconcile modal and the
 * timeline's in-place session.
 *
 * The two anchors are trusted, so they sit on top as plain facts (going from
 * the previous balance to the recorded one). Their gap is the change the
 * period has to account for, and the selection total underneath is the thing
 * being matched — so it, not the recorded balance, turns green when it lands
 * on that difference (and red while it hasn't).
 */
export const ReconcileMathSummary = ({
  baselineDate,
  baselineBalance,
  recordedDate,
  needsStartingBalance,
  selectedSum,
  recordedBalance,
  isBalanced,
  pending,
  isCreditCard,
  currency,
}: Props) => {
  const formatMoney = useMoneyFormatter();
  const fmt = (cents: number) =>
    formatMoney(Money.fromInt(toDisplayBalance(cents, isCreditCard), currency));
  // Explicit sign so the running change and the gap it has to close both read
  // as signed deltas. The sign follows the displayed value, not the stored one.
  const fmtSigned = (cents: number) => {
    const display = toDisplayBalance(cents, isCreditCard);
    const magnitude = formatMoney(Money.fromInt(Math.abs(display), currency));
    if (display > 0) return `+${magnitude}`;
    if (display < 0) return `−${magnitude}`;
    return magnitude;
  };

  // The gap between the two trusted anchors — the amount the selection must
  // sum to. Null when there's no baseline to measure from.
  const difference = needsStartingBalance
    ? null
    : recordedBalance - baselineBalance;
  // How much the selection is still short of (or over) the difference — the
  // actionable number, since a specific remainder often maps to one unpicked
  // transaction. Zero exactly when the session balances.
  const remaining = difference === null ? null : difference - selectedSum;

  // Colour the selection and its remainder: green once they match the
  // difference, red while they don't, plain while sums are still streaming in.
  const selectedColor =
    pending || needsStartingBalance
      ? undefined
      : isBalanced
        ? 'text-positive'
        : 'text-destructive';

  return (
    <div className="bg-muted rounded-lg p-3 text-sm">
      {/* balance 1 → balance 2: both trusted anchors */}
      <div className="flex justify-between">
        <span className="text-muted-foreground">
          Previous balance{baselineDate ? ` (${baselineDate})` : ''}
        </span>
        <span className="tabular-nums">
          {needsStartingBalance ? '—' : fmt(baselineBalance)}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-muted-foreground">
          Recorded balance{recordedDate ? ` (${recordedDate})` : ''}
        </span>
        <span className="tabular-nums">{fmt(recordedBalance)}</span>
      </div>
      {/* the gap to close, then the running selection total that closes it */}
      <div className="border-border mt-2 flex justify-between border-t pt-2">
        <span className="text-muted-foreground">Difference</span>
        <span className="tabular-nums">
          {difference === null ? '—' : fmtSigned(difference)}
        </span>
      </div>
      <div className={cn('flex justify-between font-semibold', selectedColor)}>
        <span>Selected total</span>
        <span className="tabular-nums">
          {pending ? '…' : needsStartingBalance ? '—' : fmtSigned(selectedSum)}
        </span>
      </div>
      {/* What's still needed to close the gap — the number to eyeball against
          the remaining transactions. */}
      <div className={cn('flex justify-between', selectedColor)}>
        <span>Remaining</span>
        <span className="tabular-nums">
          {pending ? '…' : remaining === null ? '—' : fmtSigned(remaining)}
        </span>
      </div>
    </div>
  );
};
